import { useEffect, useState } from "react";
import axios from "axios";
import AuthLayout from "@container/layout/auth";
import { Card, CardContent } from "@component/card";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Label } from "@component/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import CompanyFilter from "../admin-hiring-activity/components/company-filter";
import { getEmailSchedulers, triggerEmailSchedulerSimulation } from "@service/admin";
import { EmailSchedulerOption } from "@type/admin";
import { useSnackbar } from "@hook/use-snackbar";
import { catchErrorLog } from "@util/sentry";
import { MailIcon, Loader2Icon, SendIcon } from "lucide-react";

const INTERNAL_RECIPIENT = "user-notif@ourteam.ai";

export default function AdminSchedulerTestPage() {
    // AuthLayout owns the SnackbarProvider, so useSnackbar must be called from
    // a child component rendered *inside* it — not from this top-level page.
    return (
        <AuthLayout>
            <SchedulerTestContent />
        </AuthLayout>
    );
}

function SchedulerTestContent() {
    const snackbar = useSnackbar();
    const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
    const [selectedSchedulerKey, setSelectedSchedulerKey] = useState<string>("");
    const [asOfDate, setAsOfDate] = useState<string>("");
    const [schedulers, setSchedulers] = useState<EmailSchedulerOption[]>([]);
    const [isLoadingSchedulers, setIsLoadingSchedulers] = useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    useEffect(() => {
        let cancelled = false;
        setIsLoadingSchedulers(true);
        getEmailSchedulers()
            .then((resp) => {
                if (cancelled) return;
                setSchedulers(resp.data?.data ?? []);
            })
            .catch((err) => {
                if (cancelled) return;
                const fallback = axios.isAxiosError(err)
                    ? err.response?.data?.message
                    : null;
                snackbar.showError(
                    "Failed to load schedulers",
                    fallback || "Please refresh the page and try again."
                );
                catchErrorLog(`getEmailSchedulers error: ${err}`);
            })
            .finally(() => {
                if (cancelled) return;
                setIsLoadingSchedulers(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const selectedScheduler = schedulers.find((s) => s.key === selectedSchedulerKey);
    const dateSupported = selectedScheduler?.supports_as_of_date !== false;

    const canSubmit = !!selectedCompanyId && !!selectedSchedulerKey && !isSubmitting;

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setIsSubmitting(true);
        try {
            const resp = await triggerEmailSchedulerSimulation({
                company_id: selectedCompanyId,
                scheduler_key: selectedSchedulerKey,
                ...(asOfDate && dateSupported ? { as_of_date: asOfDate } : {}),
            });
            if (resp.data?.code === 200) {
                snackbar.showSuccess(
                    "Test email queued",
                    `Sent to ${INTERNAL_RECIPIENT}.`
                );
                setSelectedSchedulerKey("");
                setAsOfDate("");
            } else {
                snackbar.showError(
                    "Failed to send test email",
                    resp.data?.errors?.[0] || resp.data?.message || "Please try again."
                );
            }
        } catch (err) {
            const message = axios.isAxiosError(err)
                ? err.response?.data?.errors?.[0] || err.response?.data?.message
                : null;
            snackbar.showError(
                "Failed to send test email",
                message || "Please try again."
            );
            catchErrorLog(`triggerEmailSchedulerSimulation error: ${err}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
                        <MailIcon className="w-5 h-5" />
                    </div>
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Scheduler Test</h1>
                        <p className="text-sm text-neutral-500">
                            Trigger a scheduled email against a chosen company. The email is sent
                            internally to <span className="font-medium">{INTERNAL_RECIPIENT}</span>.
                        </p>
                    </div>
                </div>

                <Card className="max-w-2xl">
                    <CardContent className="p-6 sm:p-8 space-y-6">
                        {/* Company */}
                        <div className="space-y-2">
                            <Label className="block text-sm font-medium text-gray-900">Company</Label>
                            <CompanyFilter
                                value={selectedCompanyId}
                                onChange={setSelectedCompanyId}
                                className="w-full sm:w-full"
                            />
                            <p className="text-xs text-neutral-500">
                                Pick the workspace whose data will populate the email.
                            </p>
                        </div>

                        {/* Scheduler */}
                        <div className="space-y-2">
                            <Label className="block text-sm font-medium text-gray-900">Scheduler</Label>
                            <Select
                                value={selectedSchedulerKey}
                                onValueChange={setSelectedSchedulerKey}
                                disabled={isLoadingSchedulers || schedulers.length === 0}
                            >
                                <SelectTrigger className="w-full h-auto py-2 [&>span]:flex-1 [&>span]:text-left [&>span]:line-clamp-none">
                                    <SelectValue
                                        placeholder={
                                            isLoadingSchedulers
                                                ? "Loading schedulers…"
                                                : schedulers.length === 0
                                                    ? "No schedulers available"
                                                    : "Select a scheduler"
                                        }
                                    />
                                </SelectTrigger>
                                <SelectContent>
                                    {schedulers.map((s) => (
                                        <SelectItem key={s.key} value={s.key}>
                                            <div className="flex flex-col py-0.5">
                                                <span className="text-sm font-medium text-gray-900">{s.label}</span>
                                                {s.description && (
                                                    <span className="text-xs text-neutral-500">{s.description}</span>
                                                )}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {selectedScheduler?.description && (
                                <p className="text-xs text-neutral-500">{selectedScheduler.description}</p>
                            )}
                        </div>

                        {/* As-of date */}
                        <div className="space-y-2">
                            <Label htmlFor="as-of-date" className="block text-sm font-medium text-gray-900">
                                As-of date <span className="text-neutral-400 font-normal">(optional)</span>
                            </Label>
                            <Input
                                id="as-of-date"
                                type="date"
                                value={asOfDate}
                                onChange={(e) => setAsOfDate(e.target.value)}
                                disabled={!selectedSchedulerKey || !dateSupported}
                                className="w-full"
                            />
                            <p className="text-xs text-neutral-500">
                                {!selectedSchedulerKey
                                    ? "Pick a scheduler to enable this option."
                                    : !dateSupported
                                        ? "This scheduler always runs against the current time."
                                        : "Leave blank to use the current time, or pick a date to evaluate as if today were that date."}
                            </p>
                        </div>

                        {/* Submit row — divider above, recipient hint left, action right */}
                        <div className="pt-4 border-t border-neutral-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <p className="text-xs text-neutral-500">
                                Email will be sent to{" "}
                                <span className="font-medium text-gray-700">{INTERNAL_RECIPIENT}</span>.
                            </p>
                            <Button
                                onClick={handleSubmit}
                                disabled={!canSubmit}
                                className="bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center gap-2 px-5"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2Icon className="w-4 h-4 animate-spin" />
                                        Sending…
                                    </>
                                ) : (
                                    <>
                                        <SendIcon className="w-4 h-4" />
                                        Send test email
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
        </div>
    );
}
